import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

import estilos from './AjustarHoras.module.css'

type Props = {
  ordenTrabajoId: string
  horasActuales: number
  /** El inicio programado de hoy, para arrancar el editor de horario donde está. */
  inicioProgramado: string | null
  /** Las horas asignadas de hoy: el punto de partida al mover el horario. */
  horasAsignadas: number
}

type Modo = 'horas' | 'horario'

/**
 * Ajustar el trabajo: las horas y el horario.
 *
 * Dos correcciones que el Gerente operativo hace en el mismo momento y desde el
 * mismo sitio, en dos pestañas:
 *
 * · HORAS — la válvula de la regla estricta. Contando solo el tiempo en el
 *   sitio, el viaje a la ferretería no cuenta, y a veces sí es trabajo: se suma
 *   —o se resta, cuando una salida deducida quedó larga—.
 *
 * · HORARIO — correr la cita. Quedó para el martes y el cliente pidió el
 *   jueves, o arrancó más tarde de lo previsto: se mueve el inicio y las horas,
 *   y la base recalcula cuándo termina sobre el horario operativo. No toca al
 *   técnico —para eso está «Cambiar técnico»—.
 *
 * El motivo es obligatorio en las dos: no es aprobar lo que alguien pidió, es
 * cambiar una cifra —o una fecha— contra la que después se mide costo, margen y
 * atraso. Lo que cambió sin explicación es lo que nadie puede defender.
 */
export function AjustarHoras({
  ordenTrabajoId,
  horasActuales,
  inicioProgramado,
  horasAsignadas,
}: Props) {
  const clienteConsultas = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const [modo, setModo] = useState<Modo>('horas')

  const [horas, setHoras] = useState('1')
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  // El horario arranca donde está hoy: así mover la cita es tocar lo que hay,
  // no volver a escribirlo desde cero.
  const inicial = partesLocales(inicioProgramado)
  const [fecha, setFecha] = useState(inicial.fecha)
  const [hora, setHora] = useState(inicial.hora)
  const [horasHorario, setHorasHorario] = useState(String(horasAsignadas || 8))

  const ajustar = useMutation({
    mutationFn: async ({ valor, razon }: { valor: number; razon: string }): Promise<number> => {
      const { data, error: fallo } = await supabase.rpc('fn_ajustar_horas', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_horas: valor,
        p_motivo: razon,
      })

      if (fallo) throw new Error(mensajeDeError(fallo))

      return Number(data)
    },
    onSuccess: () => void invalidar(),
  })

  const reprogramar = useMutation({
    mutationFn: async ({ inicio, cuantas, razon }: { inicio: string; cuantas: number; razon: string }) => {
      const { error: fallo } = await supabase.rpc('fn_reprogramar_horario', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_inicio: inicio,
        p_horas: cuantas,
        p_motivo: razon,
      })

      if (fallo) throw new Error(mensajeDeError(fallo))
    },
    onSuccess: () => void invalidar(),
  })

  async function invalidar() {
    await Promise.all(
      [['ordenes-servicio'], ['orden-servicio'], ['trazabilidad'], ['cronograma'], ['visitas']].map(
        (clave) => clienteConsultas.invalidateQueries({ queryKey: clave }),
      ),
    )
    cerrar()
  }

  function cerrar() {
    setAbierto(false)
    setMotivo('')
    setHoras('1')
    setError(null)
  }

  // El inicio que se va a guardar, en ISO. Se arma igual que en Programar para
  // que el ida y vuelta con los campos sea el mismo.
  const inicioISO = useMemo(() => {
    if (!fecha || !hora) return null
    const d = new Date(`${fecha}T${hora}:00`)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }, [fecha, hora])

  // El fin lo calcula la base sobre el horario operativo —salta el almuerzo,
  // para al cerrar—. Se muestra antes de guardar para que se vea cuándo termina.
  const { data: fin = null, isFetching: calculandoFin } = useQuery({
    queryKey: ['fin-operativo', inicioISO, horasHorario],
    enabled: abierto && modo === 'horario' && Boolean(inicioISO) && Number(horasHorario) > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Date | null> => {
      const { data, error: fallo } = await supabase.rpc('fn_fin_operativo', {
        p_inicio: inicioISO!,
        p_horas: Number(horasHorario),
      })
      if (fallo || !data) return null
      return new Date(data as string)
    },
  })

  async function aplicarHoras(signo: 1 | -1) {
    setError(null)
    try {
      await ajustar.mutateAsync({ valor: signo * Number(horas), razon: motivo.trim() })
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible ajustar las horas.')
    }
  }

  async function aplicarHorario() {
    setError(null)
    if (!inicioISO) return setError('Diga el día y la hora de inicio.')
    try {
      await reprogramar.mutateAsync({
        inicio: inicioISO,
        cuantas: Number(horasHorario),
        razon: motivo.trim(),
      })
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible mover el horario.')
    }
  }

  // Cerrado es un enlace discreto: ajustar no es lo normal, y un botón grande
  // invitaría a usarlo como si lo fuera.
  if (!abierto) {
    return (
      <button type="button" className={estilos.abrir} onClick={() => setAbierto(true)}>
        Ajustar horas u horario
      </button>
    )
  }

  const motivoListo = motivo.trim().length >= 10
  const horasListo = motivoListo && Number(horas) > 0
  const horarioListo = motivoListo && Boolean(inicioISO) && Number(horasHorario) > 0

  return (
    <div className={estilos.zona}>
      <div className={estilos.pestanas}>
        <button
          type="button"
          className={`${estilos.pestana} ${modo === 'horas' ? estilos.activa : ''}`}
          onClick={() => {
            setModo('horas')
            setError(null)
          }}
        >
          Horas
        </button>
        <button
          type="button"
          className={`${estilos.pestana} ${modo === 'horario' ? estilos.activa : ''}`}
          onClick={() => {
            setModo('horario')
            setError(null)
          }}
        >
          Horario
        </button>
      </div>

      {modo === 'horas' ? (
        <>
          <p className={estilos.titulo}>
            Sumar o restar horas · hoy tiene <b>{horasActuales}</b>
          </p>

          <div className={estilos.campos}>
            <label className={estilos.campoHoras}>
              <span className={estilos.etiqueta}>Horas</span>
              <input
                type="number"
                min={0.25}
                step="0.25"
                value={horas}
                onChange={(e) => setHoras(e.target.value)}
                className={estilos.numero}
              />
            </label>

            <label className={estilos.campoMotivo}>
              <span className={estilos.etiqueta}>Por qué</span>
              <textarea
                rows={2}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Por ejemplo: fue por material, cuarenta minutos"
                className={estilos.area}
              />
            </label>
          </div>

          {error && <p className={estilos.error}>{error}</p>}

          <div className={estilos.acciones}>
            <button type="button" className={estilos.cancelar} onClick={cerrar}>
              Cancelar
            </button>
            <button
              type="button"
              className={estilos.restar}
              onClick={() => void aplicarHoras(-1)}
              disabled={!horasListo || ajustar.isPending}
            >
              Restar
            </button>
            <button
              type="button"
              className={estilos.sumar}
              onClick={() => void aplicarHoras(1)}
              disabled={!horasListo || ajustar.isPending}
            >
              Sumar
            </button>
          </div>
        </>
      ) : (
        <>
          <p className={estilos.titulo}>Mover la cita. El técnico no cambia aquí.</p>

          <div className={estilos.horario}>
            <label className={estilos.campoHorario}>
              <span className={estilos.etiqueta}>Día</span>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className={estilos.area}
              />
            </label>

            <label className={estilos.campoHorario}>
              <span className={estilos.etiqueta}>Hora</span>
              <input
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                className={estilos.area}
              />
            </label>

            <label className={estilos.campoHorario}>
              <span className={estilos.etiqueta}>Horas</span>
              <input
                type="number"
                min={0.5}
                step="0.5"
                value={horasHorario}
                onChange={(e) => setHorasHorario(e.target.value)}
                className={estilos.numero}
              />
            </label>
          </div>

          <p className={estilos.termina}>
            Termina{' '}
            <b>
              {calculandoFin && !fin
                ? '…'
                : fin
                  ? fin.toLocaleString('es-CO', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : '—'}
            </b>
          </p>

          <label className={estilos.campoMotivo}>
            <span className={estilos.etiqueta}>Por qué se mueve</span>
            <textarea
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Por ejemplo: el cliente pidió pasarlo al jueves"
              className={estilos.area}
            />
          </label>

          {error && <p className={estilos.error}>{error}</p>}

          <div className={estilos.acciones}>
            <button type="button" className={estilos.cancelar} onClick={cerrar}>
              Cancelar
            </button>
            <button
              type="button"
              className={estilos.sumar}
              onClick={() => void aplicarHorario()}
              disabled={!horarioListo || reprogramar.isPending}
            >
              Guardar horario
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Un instante ISO partido en los campos `date` y `time`, en hora local.
 *
 * Es el inverso de `new Date(`${fecha}T${hora}:00`)` que arma el ISO al
 * guardar: se lee y se escribe en la misma hora del navegador —el de la
 * oficina, en Colombia— para que el valor no se corra de zona en el viaje.
 * Sin inicio programado arranca vacío.
 */
function partesLocales(iso: string | null): { fecha: string; hora: string } {
  if (!iso) return { fecha: '', hora: '' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { fecha: '', hora: '' }
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    fecha: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    hora: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}
